import { createSlice } from '@reduxjs/toolkit';
import { PayloadAction } from '@reduxjs/toolkit';
import { initialState } from './initialState';
import { ApiExError, apiReponseErrorIncludes } from '@shared';
import {
  containsBrackets,
  parsePolicyExtractResponse,
  stripBeforeAndAfterBrackets,
} from '../util/policyExtractorParseResponse';
import { postProcessParsedResponse } from '../util/policyExtractorResponsePostProcess';
import { policyExtractorAggregateResults } from '../util/policyExtractorAggregateResults';

function handleCompletedResponse(
  state: PolicyExtractorState,
  payload: PolicyExtractResponseDbRecord,
) {
  state.responses = [...state.responses, payload];

  let results = payload.results ?? '';
  if (!containsBrackets(results)) {
    return addErrorMessageImpl(state, 'Invalid response format');
  }
  results = stripBeforeAndAfterBrackets(results);

  let policyAnalysisObject;
  try {
    policyAnalysisObject = parsePolicyExtractResponse(results);
  } catch (error) {
    return addErrorMessageImpl(state, 'Unable to parse response');
  }

  state.parsedResponses = [...state.parsedResponses, policyAnalysisObject];
}

function handleErrorResponse(state: PolicyExtractorState, error: string | undefined) {
  console.warn('Error response:', error);
  state.errors = [...state.errors, error ?? 'An error occurred during processing.'];
}

function addErrorMessageImpl(state: PolicyExtractorState, message: string) {
  state.errors = [...state.errors, message];
}

function resetStateImpl(state: PolicyExtractorState) {
  state.status = null;
  state.errors = [];
  state.fileBucket = '';
  state.fileKey = '';
  state.fileUrl = '';
  state.requestId = '';
  state.currentRunPollCount = 0;
  state.pollFailureCount = 0;

  state.responses = [];
  state.parsedResponses = [];
  state.processedResponse = null;
  state.policyType = null;

  state.uploadMode = 'pdf';
  state.imagePresignedUrls = [];
  state.imageBucket = '';
  state.imageKeys = [];
}

const MAX_POLL_COUNT = 60;

// A failed poll is usually transient (Lambda cold start, or the request row not being readable
// yet when the extract POST timed out before inserting it). Failing on the first one stops
// polling for good and hides the real error the worker writes minutes later, so keep polling
// until several polls in a row fail.
const MAX_CONSECUTIVE_POLL_FAILURES = 3;

const policyExtractorSlice = createSlice({
  name: 'policyExtractor',
  initialState: initialState.policyExtractor,
  reducers: {
    setStatus(state, action: PayloadAction<PolicyExtractorStatus>) {
      state.status = action.payload;
    },
    resetPolicyExtractorState(state) {
      resetStateImpl(state);
    },
    addErrorMessage(state, action: PayloadAction<string>) {
      addErrorMessageImpl(state, action.payload);
    },
    postPolicyDocumentExtractRequest(
      state,
      action: PayloadAction<PolicyUploadTextAnalysisRequest>,
    ) {
      state.status = 'initiated';
      state.requestId = action.payload.requestId;
    },
    postPolicyDocumentExtractFailure(state, action: PayloadAction<ApiExError>) {
      // If request times out, we should still poll for results since server may be processing
      const isTimeout =
        action.payload.message?.includes('request timed out') ||
        action.payload.message?.includes('NetworkError') ||
        action.payload.statusCode === '504';

      if (isTimeout && state.requestId) {
        state.status = 'polling';
      } else {
        state.status = 'error';
        if (apiReponseErrorIncludes(action.payload, 'Extracted text is too short')) {
          addErrorMessageImpl(state, 'Extracted text is too short');
        } else {
          addErrorMessageImpl(state, 'Error extracting policy');
        }
      }
    },
    postPolicyDocumentExtractResponse(state, action: PayloadAction<{ success: boolean }>) {
      state.status = 'polling';
      // requestId is already stored from postPolicyDocumentExtractRequest
    },
    fetchPolicyExtractedDataByRequestIdRequest(
      state,
      _action: PayloadAction<PolicyExtractRequestIdProp>,
    ) {
      state.status = 'polling';
    },
    fetchPolicyExtractedDataByRequestIdFailure(state, _action: PayloadAction<ApiExError>) {
      state.pollFailureCount += 1;
      if (state.pollFailureCount < MAX_CONSECUTIVE_POLL_FAILURES) {
        state.status = 'polling';
        return;
      }
      state.status = 'error';
      addErrorMessageImpl(state, 'Error retrieving policy extracted data');
    },
    fetchPolicyExtractedDataByRequestIdResponse(
      state,
      action: PayloadAction<PolicyExtractResponseDbRecord>,
    ) {
      state.pollFailureCount = 0;

      switch (action.payload.status) {
        case 'completed':
          state.currentRunPollCount = 0;
          state.status = 'allRunsComplete';
          state.policyType = action.payload.policyType;
          handleCompletedResponse(state, action.payload);
          try {
            const policyAnalysisObject = policyExtractorAggregateResults(state.parsedResponses);
            state.processedResponse = postProcessParsedResponse(
              policyAnalysisObject,
              action.payload.policyType,
            );
          } catch (error) {
            addErrorMessageImpl(state, 'Processing error');
            state.status = 'error';
          }
          break;
        case 'error':
          state.currentRunPollCount = 0;
          state.status = 'error';
          handleErrorResponse(state, action.payload.error);
          break;
        default:
          state.currentRunPollCount += 1;
      }

      if (state.currentRunPollCount > MAX_POLL_COUNT) {
        state.status = 'error';
        addErrorMessageImpl(state, 'Request timed out.');
      }
    },
    postPresignedUrlForClientByClientIdRequest(
      state,
      _action: PayloadAction<PolicyUploadPresignedUrlRequestProps>,
    ) {
      resetStateImpl(state);
      state.status = 'initiated';
    },
    postPresignedUrlForClientByClientIdFailure(state, action: PayloadAction<ApiExError>) {
      state.status = 'error';
      addErrorMessageImpl(state, 'Error uploading file. Please make sure the file is a PDF.');
    },
    postPresignedUrlForClientByClientIdResponse(
      state,
      action: PayloadAction<PolicyUploadPresignedUrlResponse>,
    ) {
      state.fileUrl = action.payload.url;
      state.fileBucket = action.payload.bucket;
      state.fileKey = action.payload.key;
    },
    setUploadMode(state, action: PayloadAction<'pdf' | 'images'>) {
      state.uploadMode = action.payload;
    },
    postPolicyImagePresignedUrlsForClientByClientIdRequest(
      state,
      action: PayloadAction<PolicyImagePresignedUrlRequestProps>,
    ) {
      resetStateImpl(state);
      state.uploadMode = 'images';
      state.status = 'initiated';
      state.requestId = action.payload.requestId;
    },
    postPolicyImagePresignedUrlsForClientByClientIdFailure(
      state,
      _action: PayloadAction<ApiExError>,
    ) {
      state.status = 'error';
      state.errors = [...state.errors, 'Error preparing image upload. Please try again.'];
    },
    postPolicyImagePresignedUrlsForClientByClientIdResponse(
      state,
      action: PayloadAction<PolicyImagePresignedUrlResponse>,
    ) {
      state.imagePresignedUrls = action.payload.items;
      state.imageBucket = action.payload.bucket;
      state.imageKeys = action.payload.items.map(item => item.key);
    },
    setImageUploadComplete(state) {
      state.status = 'fileUploadComplete';
    },
  },
});

export const {
  resetPolicyExtractorState,
  fetchPolicyExtractedDataByRequestIdRequest,
  fetchPolicyExtractedDataByRequestIdFailure,
  fetchPolicyExtractedDataByRequestIdResponse,
  postPresignedUrlForClientByClientIdRequest,
  postPresignedUrlForClientByClientIdFailure,
  postPresignedUrlForClientByClientIdResponse,
  setStatus,
  addErrorMessage,
  postPolicyDocumentExtractRequest,
  postPolicyDocumentExtractFailure,
  postPolicyDocumentExtractResponse,
  setUploadMode,
  postPolicyImagePresignedUrlsForClientByClientIdRequest,
  postPolicyImagePresignedUrlsForClientByClientIdFailure,
  postPolicyImagePresignedUrlsForClientByClientIdResponse,
  setImageUploadComplete,
} = policyExtractorSlice.actions;

export const policyExtractorSliceReducer = policyExtractorSlice.reducer;
